import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DollarSign, Plus, Loader2, Receipt, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { reimbursementAPI } from '@/lib/api';
import ReimbursementFormDialog from '@/components/reimbursements/ReimbursementFormDialog';
import ReimbursementDetailDialog from '@/components/reimbursements/ReimbursementDetailDialog';
import { CATEGORY_LABEL, STATUS_META, money } from '@/components/reimbursements/constants';

export default function MyReimbursementsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await reimbursementAPI.mine();
      setRequests(r.data.requests || []);
    } catch {
      toast.error('Failed to load reimbursements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChanged = (updated) => {
    setSelected(updated);
    setRequests((prev) => prev.map((x) => (x.request_id === updated.request_id ? updated : x)));
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="my-reimbursements-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 flex items-center gap-2">
            <DollarSign size={22} className="text-emerald-600" /> Reimbursements
          </h1>
          <p className="text-sm text-slate-500">Submit a cost for KR to pay you back — events, receipts, supplies and more. Attach your receipts.</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-reimbursement-btn">
          <Plus size={16} className="mr-1.5" /> New Reimbursement
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : requests.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-slate-500">
          <Receipt size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No reimbursement requests yet. Tap "New Reimbursement" to submit one with your receipts.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {requests.map((r) => {
            const meta = STATUS_META[r.status] || {};
            return (
              <button
                key={r.request_id}
                onClick={() => setSelected(r)}
                className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm hover:border-slate-300 transition"
                data-testid={`reimbursement-card-${r.request_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 leading-tight">{r.title}</h3>
                  <Badge className={`text-[10px] border shrink-0 ${meta.cls || ''}`}>{meta.label || r.status}</Badge>
                </div>
                <p className="text-lg font-bold text-emerald-700 mt-1">{money(r.amount)}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                  <span>{CATEGORY_LABEL[r.category] || r.category}</span>
                  <span className="flex items-center gap-1"><Receipt size={12} /> {(r.receipts || []).length}</span>
                  {r.expense_date && <span className="flex items-center gap-1"><Calendar size={12} /> {r.expense_date}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <ReimbursementFormDialog open={showForm} onOpenChange={setShowForm} onDone={load} />
      <ReimbursementDetailDialog
        open={!!selected}
        onOpenChange={(v) => { if (!v) setSelected(null); }}
        request={selected}
        admin={false}
        onChanged={onChanged}
      />
    </div>
  );
}
